import { Injectable } from '@angular/core';
import { Action, Actions, ofActionSuccessful, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { filter, mergeMap, tap } from 'rxjs/operators';
import { DirectoriesService, OrganisationEntryRemoteDto } from '../../../../../generated/swagger';
import { SessionUpdated } from '../../../session/session.actions';
import { DirectoryOrganisationsLoaded, LoadDirectoryOrganisations } from './directory-organisation-data.actions';

export interface DirectoryOrganisationDataStateModel {
   organisation01Entries: OrganisationEntryRemoteDto[];
   organisation02Entries: OrganisationEntryRemoteDto[];
   organisation03Entries: OrganisationEntryRemoteDto[];
   organisation04Entries: OrganisationEntryRemoteDto[];
   organisation05Entries: OrganisationEntryRemoteDto[];
   organisation06Entries: OrganisationEntryRemoteDto[];
   organisation07Entries: OrganisationEntryRemoteDto[];
   organisation08Entries: OrganisationEntryRemoteDto[];
   organisation09Entries: OrganisationEntryRemoteDto[];
   organisation10Entries: OrganisationEntryRemoteDto[];
   organisation11Entries: OrganisationEntryRemoteDto[];
   organisation12Entries: OrganisationEntryRemoteDto[];
   organisation13Entries: OrganisationEntryRemoteDto[];
   organisation14Entries: OrganisationEntryRemoteDto[];
   organisation15Entries: OrganisationEntryRemoteDto[];
   organisation16Entries: OrganisationEntryRemoteDto[];
   organisation17Entries: OrganisationEntryRemoteDto[];
   organisation18Entries: OrganisationEntryRemoteDto[];
   organisation19Entries: OrganisationEntryRemoteDto[];
   organisation20Entries: OrganisationEntryRemoteDto[];
}

@State<DirectoryOrganisationDataStateModel>({
   name: 'directoryOrganisationData',
   defaults: {
      organisation01Entries: [],
      organisation02Entries: [],
      organisation03Entries: [],
      organisation04Entries: [],
      organisation05Entries: [],
      organisation06Entries: [],
      organisation07Entries: [],
      organisation08Entries: [],
      organisation09Entries: [],
      organisation10Entries: [],
      organisation11Entries: [],
      organisation12Entries: [],
      organisation13Entries: [],
      organisation14Entries: [],
      organisation15Entries: [],
      organisation16Entries: [],
      organisation17Entries: [],
      organisation18Entries: [],
      organisation19Entries: [],
      organisation20Entries: [],
   },
})
@Injectable()
export class DirectoryOrganisationDataState {
   constructor(private directoriesService: DirectoriesService, private actions$: Actions, private store: Store) {
      actions$
         .pipe(
            ofActionSuccessful(SessionUpdated),
            filter((x: SessionUpdated) => x.sessionInfo.isAuthenticated),
            tap(() => store.dispatch(new LoadDirectoryOrganisations()))
         )
         .subscribe();
   }

   @Selector([DirectoryOrganisationDataState])
   public static getOrganisationEntries(state: DirectoryOrganisationDataStateModel): (organisationUnitNumber: string) => OrganisationEntryRemoteDto[] {
      return (organisationUnitNumber: string) => {
         if (organisationUnitNumber === '01') {
            return state.organisation01Entries;
         }
         if (organisationUnitNumber === '02') {
            return state.organisation02Entries;
         }
         if (organisationUnitNumber === '03') {
            return state.organisation03Entries;
         }
         if (organisationUnitNumber === '04') {
            return state.organisation04Entries;
         }
         if (organisationUnitNumber === '05') {
            return state.organisation05Entries;
         }
         if (organisationUnitNumber === '06') {
            return state.organisation06Entries;
         }
         if (organisationUnitNumber === '07') {
            return state.organisation07Entries;
         }
         if (organisationUnitNumber === '08') {
            return state.organisation08Entries;
         }
         if (organisationUnitNumber === '09') {
            return state.organisation09Entries;
         }
         if (organisationUnitNumber === '10') {
            return state.organisation10Entries;
         }
         if (organisationUnitNumber === '11') {
            return state.organisation11Entries;
         }
         if (organisationUnitNumber === '12') {
            return state.organisation12Entries;
         }
         if (organisationUnitNumber === '13') {
            return state.organisation13Entries;
         }
         if (organisationUnitNumber === '14') {
            return state.organisation14Entries;
         }
         if (organisationUnitNumber === '15') {
            return state.organisation15Entries;
         }
         if (organisationUnitNumber === '16') {
            return state.organisation16Entries;
         }
         if (organisationUnitNumber === '17') {
            return state.organisation17Entries;
         }
         if (organisationUnitNumber === '18') {
            return state.organisation18Entries;
         }
         if (organisationUnitNumber === '19') {
            return state.organisation19Entries;
         }
         if (organisationUnitNumber === '20') {
            return state.organisation20Entries;
         }
         return null;
      };
   }

   @Selector([DirectoryOrganisationDataState.getOrganisationEntries])
   public static getOrganisationEntryForOrganisationUnit(
      getOrganisationEntries: (organisationUnitNumber: string) => OrganisationEntryRemoteDto[]
   ): (organisationUnitNumber: string, subscriberStructureValue: number) => OrganisationEntryRemoteDto {
      return (organisationUnitNumber: string, subscriberStructureValue: number) => {
         const organisationEntries = getOrganisationEntries(organisationUnitNumber);
         if (_.isNil(organisationEntries)) {
            return null;
         }
         const targetOrganisationEntry = _(organisationEntries)
            .filter((x) => x.id === subscriberStructureValue)
            .first();
         return targetOrganisationEntry;
      };
   }

   @Action(LoadDirectoryOrganisations)
   public loadDirectoryOrganisations(ctx: StateContext<DirectoryOrganisationDataStateModel>, action: LoadDirectoryOrganisations) {
      return this.directoriesService
         .directoryOrganisationData()
         .pipe(mergeMap((x) => ctx.dispatch(new DirectoryOrganisationsLoaded(x.resultMessageDirectoryOrganisations))));
   }

   @Action(DirectoryOrganisationsLoaded)
   public directoryOrganisationsLoaded(ctx: StateContext<DirectoryOrganisationDataStateModel>, action: DirectoryOrganisationsLoaded) {
      ctx.patchState({
         organisation01Entries: action.organisationsData.organisation1Entries,
         organisation02Entries: action.organisationsData.organisation2Entries,
         organisation03Entries: action.organisationsData.organisation3Entries,
         organisation04Entries: action.organisationsData.organisation4Entries,
         organisation05Entries: action.organisationsData.organisation5Entries,
         organisation06Entries: action.organisationsData.organisation6Entries,
         organisation07Entries: action.organisationsData.organisation7Entries,
         organisation08Entries: action.organisationsData.organisation8Entries,
         organisation09Entries: action.organisationsData.organisation9Entries,
         organisation10Entries: action.organisationsData.organisation10Entries,
         organisation11Entries: action.organisationsData.organisation11Entries,
         organisation12Entries: action.organisationsData.organisation12Entries,
         organisation13Entries: action.organisationsData.organisation13Entries,
         organisation14Entries: action.organisationsData.organisation14Entries,
         organisation15Entries: action.organisationsData.organisation15Entries,
         organisation16Entries: action.organisationsData.organisation16Entries,
         organisation17Entries: action.organisationsData.organisation17Entries,
         organisation18Entries: action.organisationsData.organisation18Entries,
         organisation19Entries: action.organisationsData.organisation19Entries,
         organisation20Entries: action.organisationsData.organisation20Entries,
      });
   }
}
